import { useState, useEffect, useRef, useId } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTheme } from '@mui/material/styles';
import { useWizardAccordionVariant } from './WizardAccordionStyleContext';

interface AsWrittenSubsectionProps {
  /** Defaults to the design's "+ Add 'as written' details" */
  label?: string;
  /** Heading shown inside the revealed box. Defaults to "As written on the object". */
  heading?: string;
  /** Auto-reveal when child fields already have content (e.g. from saved data). */
  defaultRevealed?: boolean;
  /**
   * When true, always render the children and hide the toggle entirely.
   * The describe variant (Artwork Details page) sets this automatically via
   * context; callers can also force it explicitly.
   */
  alwaysOpen?: boolean;
  children: React.ReactNode;
}

/**
 * Subdued left-bordered subsection used by Title, Signature, and Creation Date
 * on the Describe Artwork page. Mirrors the design's "As written on the object"
 * pattern: a single reveal button that expands into a styled card containing
 * the related fields.
 */
export const AsWrittenSubsection = ({
  label = "+ Add 'as written' details",
  heading = 'As written on the object',
  defaultRevealed = false,
  alwaysOpen,
  children,
}: AsWrittenSubsectionProps) => {
  const theme = useTheme();
  const variant = useWizardAccordionVariant();
  // The Artwork Details page always shows these fields per the Hi‑Fi design.
  const forceOpen = alwaysOpen ?? variant === 'describe';
  const [revealed, setRevealed] = useState(defaultRevealed);
  const userToggledRef = useRef(false);
  const contentId = useId();

  useEffect(() => {
    if (!userToggledRef.current && defaultRevealed) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- prop-to-state sync, guarded
      setRevealed(true);
    }
  }, [defaultRevealed]);

  const handleToggle = () => {
    userToggledRef.current = true;
    setRevealed((prev) => !prev);
  };

  const open = forceOpen || revealed;

  return (
    <Box sx={{ mt: 2 }}>
      {!forceOpen && (
        <Button
          onClick={handleToggle}
          startIcon={revealed ? <RemoveIcon /> : <AddIcon />}
          size="small"
          aria-expanded={revealed}
          aria-controls={revealed ? contentId : undefined}
          sx={{ textTransform: 'none' }}
        >
          {revealed ? `Hide '${heading.toLowerCase()}'` : label}
        </Button>
      )}
      {open && (
        <Box
          id={contentId}
          sx={{
            mt: forceOpen ? 0 : 1,
            p: 2,
            borderLeft: `3px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 1,
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', mb: 1.5, letterSpacing: '0.08em' }}
          >
            {heading}
          </Typography>
          {children}
        </Box>
      )}
    </Box>
  );
};
